import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaFileExcel,
  FaPrint,
  FaFilter,
  FaTimes,
  FaBox,
} from "react-icons/fa";

import {
  CalendarRange,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const FinancialYearEnquiryReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin =
    getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const storedBranchType =
    localStorage.getItem(
      "branchType"
    ) || "HO";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          storedBranchType,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        storedBranchType,
        userId,
      ]
    );

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchType,
    setBranchType,
  ] = useState(
    storedBranchType || "HO"
  );

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId || "8"
  );

  const [
    financialYear,
    setFinancialYear,
  ] = useState(
    finYearId || ""
  );

  const [
    status,
    setStatus,
  ] = useState("");

  const [
    store,
    setStore,
  ] = useState("");

  const [
    supplier,
    setSupplier,
  ] = useState("");

  /* =========================================================
     DROPDOWN STATE
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    supplierList,
    setSupplierList,
  ] = useState([]);

  const [
    financialYearList,
    setFinancialYearList,
  ] = useState([]);

  /* =========================================================
     RESULT STATE
  ========================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     TEMPLATE DESIGN CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     LOAD DROPDOWNS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/financeYrs_dropdownRpt",
        {
          params: {
            branchid:
              branchId ||
              storedBranchId ||
              "8",

            branchtype:
              branchType ||
              storedBranchType ||
              "HO",
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const stores =
          res.data.data
            ?.stores ||
          res.data.stores ||
          [];

        const suppliers =
          res.data.data
            ?.suppliers ||
          res.data.suppliers ||
          [];

        const years =
          res.data.data
            ?.financialYears ||
          res.data.financialYears ||
          res.data.records ||
          [];

        const defaultStoreId =
          res.data.data
            ?.defaultStoreId ||
          res.data.defaultStoreId;

        setBranchList(
          stores
        );

        setSupplierList(
          suppliers
        );

        if (
          years.length > 0
        ) {
          setFinancialYearList(
            years
          );
        } else {
          setFinancialYearList([
            {
              value: "1",
              text: "2022-2023",
            },
            {
              value: "2",
              text: "2021-2022",
            },
          ]);
        }

        if (
          defaultStoreId &&
          !store
        ) {
          setStore(
            defaultStoreId.toString()
          );

          setBranchId(
            defaultStoreId.toString()
          );
        }
      })
      .catch((err) => {
        console.error(
          "Dropdown fetch failed",
          err
        );

        toast.error(
          "Failed to load dropdowns"
        );
      })
      .finally(() => {
        setLoading(false);
      });

    // Preserving original dependency behavior
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchType]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    if (!branchId) {
      toast.error(
        "Branch ID is required"
      );

      return;
    }

    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/financialYrs_rpt",
        {
          params: {
            financialYearId:
              financialYear ||
              finYearId ||
              "",

            branchid:
              branchId,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const rows =
          res.data.records ||
          [];

        setData(
          Array.isArray(rows)
            ? rows
            : []
        );

        toast.success(
          `Loaded ${
            Array.isArray(rows)
              ? rows.length
              : 0
          } records`
        );
      })
      .catch((err) => {
        console.error(
          "Financial year report fetch failed",
          err
        );

        toast.error(
          err.response?.data
            ?.error ||
            "Failed to fetch report"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     EXPORT EXCEL
     REAL EXISTING FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleExcelDownload = () => {
    if (
      data.length === 0
    ) {
      toast.error(
        "No data to export"
      );

      return;
    }

    const payload = {
      branchid:
        branchId ||
        storedBranchId ||
        "8",

      branchtype:
        branchType === "HO"
          ? "H"
          : "B",

      finyearid:
        financialYear ||
        finYearId ||
        "",

      supplierid:
        supplier ||
        "%",

      storeid:
        store ||
        "%",

      status:
        status ||
        "%",
    };

    axios
      .get(
        "https://nissi-india.com/api/po_fillGridFull",
        {
          params:
            payload,

          headers:
            commonHeaders,

          responseType:
            "blob",
        }
      )
      .then((res) => {
        const url =
          window.URL.createObjectURL(
            new Blob([
              res.data,
            ])
          );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          url;

        link.setAttribute(
          "download",
          `FinancialYearAdjustment_${new Date()
            .toISOString()
            .slice(
              0,
              10
            )}.xlsx`
        );

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        window.URL.revokeObjectURL(
          url
        );

        toast.success(
          "Data exported successfully"
        );
      })
      .catch((err) => {
        console.error(
          "Excel export failed",
          err
        );

        toast.error(
          "Failed to export Excel"
        );
      });
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    const payload = {
      branchid:
        branchId ||
        storedBranchId ||
        "8",

      branchtype:
        branchType === "HO"
          ? "H"
          : "B",

      finyearid:
        financialYear ||
        finYearId ||
        "",

      supplierid:
        supplier ||
        "%",

      storeid:
        store ||
        "%",

      status:
        status ||
        "%",
    };

    const query =
      new URLSearchParams(
        payload
      ).toString();

    window.open(
      `/inventory/FinancialYearEnquiryReport_Print?${query}`,
      "_blank"
    );
  };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setFinancialYear(
      finYearId || ""
    );

    setStatus("");

    setStore("");

    setSupplier("");

    setBranchId(
      storedBranchId ||
        "8"
    );

    setData([]);
  };

  /* =========================================================
     TOTAL AMOUNT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const totalAmount =
    useMemo(
      () =>
        data.reduce(
          (
            sum,
            item
          ) =>
            sum +
            (parseFloat(
              item.totalamount ||
                item.amount ||
                item.fld_amount ||
                item.TotalAmount ||
                0
            ) ||
              0),
          0
        ),
      [data]
    );

  /* =========================================================
     FORMAT NUMBER
  ========================================================= */

  const formatNumber = (
    value
  ) => {
    return parseFloat(
      value || 0
    ).toLocaleString(
      "en-IN",
      {
        maximumFractionDigits:
          2,
      }
    );
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Financial Year Closing Adjustment Entry Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <CalendarRange className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Financial Year Closing Adjustment Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  View and manage financial year closing adjustment entries
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT */}

              <button
                type="button"
                onClick={
                  handleExcelDownload
                }
                disabled={
                  data.length === 0
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaFileExcel
                  size={14}
                />

                Export Excel
              </button>

              {/* FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* FINANCIAL YEAR */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Financial Year
                  </label>

                  <select
                    value={
                      financialYear
                    }
                    onChange={(e) =>
                      setFinancialYear(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Year
                    </option>

                    {financialYearList.map(
                      (
                        year,
                        index
                      ) => (
                        <option
                          key={
                            year.id ||
                            year.value ||
                            index
                          }
                          value={
                            year.id ||
                            year.value ||
                            ""
                          }
                        >
                          {year.name ||
                            year.text ||
                            year.financialyear ||
                            ""}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* STATUS */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Status
                  </label>

                  <select
                    value={
                      status
                    }
                    onChange={(e) =>
                      setStatus(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Status
                    </option>

                    <option value="Active">
                      Active
                    </option>

                    <option value="Cancel">
                      Canceled
                    </option>

                    <option value="Closed">
                      Closed
                    </option>
                  </select>
                </div>

                {/* STORE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Store
                  </label>

                  <select
                    value={
                      store
                    }
                    onChange={(e) => {
                      setStore(
                        e.target.value
                      );

                      if (
                        e.target.value
                      ) {
                        setBranchId(
                          e.target.value
                        );
                      }
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Stores
                    </option>

                    {branchList.map(
                      (
                        b,
                        index
                      ) => (
                        <option
                          key={
                            b.value ||
                            b.fld_storeid ||
                            index
                          }
                          value={
                            b.value ||
                            b.fld_storeid ||
                            ""
                          }
                        >
                          {b.text ||
                            b.fld_storename ||
                            ""}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* SUPPLIER */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Supplier
                  </label>

                  <select
                    value={
                      supplier
                    }
                    onChange={(e) =>
                      setSupplier(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Suppliers
                    </option>

                    {supplierList.map(
                      (
                        s,
                        index
                      ) => (
                        <option
                          key={
                            s.value ||
                            s.fld_supplierid ||
                            index
                          }
                          value={
                            s.value ||
                            s.fld_supplierid ||
                            ""
                          }
                        >
                          {s.text ||
                            s.fld_suppliername ||
                            ""}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION ROW
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  disabled={
                    data.length === 0
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
            SAME FIRST PAGE DESIGN
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Financial Year Adjustment Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {data.length > 0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading financial year adjustment records...
              </p>
            </div>
          ) : data.length > 0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Document No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Supplier
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Amount (₹)
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Status
                    </th>
                  </tr>
                </thead>

                {/* BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => {
                      const rowStatus =
                        item.status ||
                        item.fld_status ||
                        "";

                      return (
                        <tr
                          key={
                            index
                          }
                          className={`transition ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >

                          {/* SERIAL */}

                          <td className="px-4 py-3 font-medium">
                            {index + 1}
                          </td>

                          {/* DOCUMENT NO */}

                          <td className="px-4 py-3">

                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {item.pono ||
                                item.docno ||
                                item.documentno ||
                                item.voucherno ||
                                "-"}
                            </span>
                          </td>

                          {/* DATE */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.podate ||
                              item.date ||
                              item.docdate ||
                              "-"}
                          </td>

                          {/* SUPPLIER */}

                          <td className="px-4 py-3 font-medium">
                            {item.suppliername ||
                              item.fld_suppliername ||
                              item.partyname ||
                              "-"}
                          </td>

                          {/* AMOUNT */}

                          <td
                            className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                              isDark
                                ? "text-green-300"
                                : "text-green-700"
                            }`}
                          >
                            ₹{" "}
                            {formatNumber(
                              item.totalamount ||
                                item.amount ||
                                item.fld_amount ||
                                item.TotalAmount ||
                                0
                            )}
                          </td>

                          {/* STATUS */}

                          <td className="px-4 py-3">

                            <span
                              className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                                String(
                                  rowStatus
                                )
                                  .toLowerCase()
                                  .includes(
                                    "active"
                                  )
                                  ? isDark
                                    ? "bg-green-900/30 text-green-300"
                                    : "bg-green-100 text-green-800"
                                  : String(
                                      rowStatus
                                    )
                                      .toLowerCase()
                                      .includes(
                                        "closed"
                                      )
                                  ? isDark
                                    ? "bg-blue-900/30 text-blue-300"
                                    : "bg-blue-100 text-blue-800"
                                  : String(
                                      rowStatus
                                    )
                                      .toLowerCase()
                                      .includes(
                                        "cancel"
                                      )
                                  ? isDark
                                    ? "bg-red-900/30 text-red-300"
                                    : "bg-red-100 text-red-800"
                                  : isDark
                                  ? "bg-gray-700 text-gray-300"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {rowStatus ||
                                "-"}
                            </span>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>

                {/* =========================================
                    TOTAL FOOTER
                ========================================= */}

                <tfoot
                  className={
                    isDark
                      ? "bg-slate-900/70"
                      : "bg-slate-100"
                  }
                >
                  <tr>

                    <td
                      colSpan="4"
                      className={`px-4 py-3 text-right text-sm font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Total
                    </td>

                    <td
                      className={`px-4 py-3 whitespace-nowrap text-right text-sm font-bold ${
                        isDark
                          ? "text-green-300"
                          : "text-green-700"
                      }`}
                    >
                      ₹{" "}
                      {formatNumber(
                        totalAmount
                      )}
                    </td>

                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaBox
                size={42}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No records found
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>

        {/* ===================================================
            SUMMARY CARD
            EXISTING SUMMARY VALUES PRESERVED
        =================================================== */}

        {data.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >

            {/* SUMMARY HEADER */}

            <div className="p-6 border-b border-gray-200 dark:border-gray-700">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <div>

                  <h3 className="text-xl font-bold">
                    Summary
                  </h3>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Financial year closing adjustment overview
                  </p>
                </div>
              </div>
            </div>

            {/* SUMMARY CONTENT */}

            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

                {/* TOTAL RECORDS */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total Records
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold text-${baseColor}-${baseShade}`}
                  >
                    {data.length}
                  </p>
                </div>

                {/* TOTAL AMOUNT */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total Amount
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-green-300"
                        : "text-green-700"
                    }`}
                  >
                    ₹{" "}
                    {formatNumber(
                      totalAmount
                    )}
                  </p>
                </div>

                {/* AVERAGE */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Average per Record
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-blue-300"
                        : "text-blue-700"
                    }`}
                  >
                    ₹{" "}
                    {formatNumber(
                      data.length > 0
                        ? totalAmount /
                            data.length
                        : 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default FinancialYearEnquiryReport;